import { getIntersectionRoot, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

// Position depends on every chapter, so reverse scrolling, fragment jumps and
// restored pages all resolve to the same place without an animation timeline.
export function journeyPosition(anchors, readingLine) {
  if (!anchors.length) return { index: 0, progress: 0 };
  if (readingLine <= anchors[0].top) return { index: 0, progress: anchors[0].progress };
  for (let i = 1; i < anchors.length; i++) {
    if (readingLine < anchors[i].top) {
      const previous = anchors[i - 1];
      const fraction = Math.max(0, Math.min(1, (readingLine - previous.top) / Math.max(1, anchors[i].top - previous.top)));
      return { index: fraction >= .5 ? i : i - 1, progress: previous.progress + (anchors[i].progress - previous.progress) * fraction };
    }
  }
  return { index: anchors.length - 1, progress: anchors.at(-1).progress };
}

class EgyptJourney extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.steps = [...this.querySelectorAll('[data-tour-step]')];
    this.paths = [...this.querySelectorAll('[data-route-segment]')];
    this.traveller = this.querySelector('[data-traveller]');
    this.toggle = this.querySelector('[data-motion-toggle]');
    this.locations = [...this.querySelectorAll('[data-location],[data-mobile-location]')];
    this.destinations = { arrival: 0 };
    this.totalLength = 0;
    this.segments = this.paths.map(path => {
      const segment = { path, start: this.totalLength, length: path.getTotalLength(), mode: path.dataset.mode };
      this.totalLength += segment.length;
      this.destinations[path.dataset.routeSegment] = this.totalLength;
      return segment;
    });
    this.schedule = () => {
      if (this.frame || !this.isConnected) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    this.connect = () => {
      this.scrollTarget?.removeEventListener('scroll', this.schedule);
      this.root = getIntersectionRoot();
      this.scrollTarget = getScrollEventTarget();
      this.scrollTarget.addEventListener('scroll', this.schedule, { passive: true });
      this.toggle.hidden = this.motion.matches || Boolean(window.Shopify?.designMode);
      this.classList.toggle('is-enhanced', !this.motion.matches && !window.Shopify?.designMode);
      this.schedule();
    };
    this.toggleMotion = () => {
      this.paused = !this.paused;
      this.toggle.setAttribute('aria-pressed', String(this.paused));
      this.toggle.textContent = this.paused ? 'Resume map motion' : 'Pause map motion';
      if (!this.paused) this.schedule();
    };
    this.toggle.addEventListener('click', this.toggleMotion);
    this.motion.addEventListener('change', this.connect);
    scrollContainerMediaQuery.addEventListener('change', this.connect);
    window.addEventListener('resize', this.schedule, { passive: true });
    window.addEventListener('pageshow', this.schedule);
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.schedule);
      this.steps.forEach(step => this.observer.observe(step));
    }
    this.connect();
  }
  update() {
    if (!this.steps.length || !this.segments.length) return;
    const rootRect = this.root?.getBoundingClientRect();
    const top = Math.max(0, rootRect?.top ?? 0);
    const bottom = Math.min(innerHeight, rootRect?.bottom ?? innerHeight);
    const line = top + (bottom - top) * .48;
    const anchors = this.steps.map(step => ({ top: step.getBoundingClientRect().top + 80, progress: this.destinations[step.dataset.destination] ?? 0 }));
    const state = journeyPosition(anchors, line);
    const step = this.steps[state.index];
    this.locations.forEach(label => { label.textContent = step.dataset.travel; });
    this.querySelectorAll('.sotp-tour__chapter-nav a').forEach(link => {
      const target = this.querySelector(link.getAttribute('href'));
      if (target?.dataset.destination === step.dataset.destination) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    if (this.paused || this.motion.matches || window.Shopify?.designMode) return;
    this.style.setProperty('--journey-progress', String(state.progress / this.totalLength));
    const segment = this.segments.find(s => state.progress <= s.start + s.length) ?? this.segments.at(-1);
    const distance = Math.max(0, Math.min(segment.length, state.progress - segment.start));
    const point = segment.path.getPointAtLength(distance);
    const ahead = segment.path.getPointAtLength(Math.min(segment.length, distance + 1));
    const behind = segment.path.getPointAtLength(Math.max(0, distance - 1));
    const angle = segment.mode === 'plane' ? Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180 / Math.PI + 90 : 0;
    this.traveller.setAttribute('transform', `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)})`);
    this.traveller.querySelector('[data-vehicle="plane"]').setAttribute('transform', `rotate(${angle.toFixed(2)})`);
    this.dataset.travelMode = segment.mode;
    this.segments.forEach(s => s.path.classList.toggle('is-travelled', state.progress >= s.start));
  }
  disconnectedCallback() {
    this.scrollTarget?.removeEventListener('scroll', this.schedule);
    this.toggle?.removeEventListener('click', this.toggleMotion);
    this.motion?.removeEventListener('change', this.connect);
    scrollContainerMediaQuery.removeEventListener('change', this.connect);
    window.removeEventListener('resize', this.schedule);
    window.removeEventListener('pageshow', this.schedule);
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.initialized = false;
  }
}
if (!customElements.get('sotp-egypt-journey')) customElements.define('sotp-egypt-journey', EgyptJourney);

// Real anchor links remain usable before JS arrives. Enhancement transfers focus
// to the destination and carries a chosen trip length into the native contact form.
document.addEventListener('click', event => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest?.('.sotp-tours a[href^="#"]');
  if (!link) return;
  const target = document.getElementById(link.hash.slice(1));
  if (!target) return;
  event.preventDefault();
  if (link.dataset.durationChoice) {
    const select = link.closest('.sotp-tours').querySelector('[data-tour-duration]');
    if (select) select.value = link.dataset.durationChoice;
  }
  history.pushState(null, '', link.hash);
  target.focus({ preventScroll: true });
  target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
});
